import React, { useState, useMemo, useCallback } from 'react';
import { ArrowLeftRight, ShieldCheck, Shield, ShieldAlert, HelpCircle, Loader, ChevronRight, ScanBarcode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllProductEvents, getProfile } from '../utils/storage';
import { getProductAssessment } from '../scoring/edcScoring';
import { fetchByBarcode, searchProducts, searchBeautyProducts } from '../services/productApi';
import { CURATED_ALTERNATIVES } from '../data/curatedAlternatives';

const SIGNAL_ORDER = { 'Elevated Signal': 3, 'Moderate Signal': 2, 'Low Signal': 1, 'Unclear': 0 };

function signalWeight(level) {
    return SIGNAL_ORDER[level] || 0;
}

function SignalBadge({ level, small }) {
    let className = 'signal-unclear';
    let Icon = HelpCircle;
    if (level === 'Low Signal') { className = 'signal-low'; Icon = ShieldCheck; }
    else if (level === 'Moderate Signal') { className = 'signal-moderate'; Icon = Shield; }
    else if (level === 'Elevated Signal') { className = 'signal-elevated'; Icon = ShieldAlert; }

    return (
        <div className={`signal-badge ${className}`} style={small ? { fontSize: 11, padding: '3px 8px' } : {}}>
            <Icon size={small ? 12 : 14} />
            <span>{level}</span>
        </div>
    );
}

export default function Swap() {
    const navigate = useNavigate();
    const profile = useMemo(() => getProfile() || {}, []);

    // Aggregate all scanned products across all dates
    const rankedProducts = useMemo(() => {
        const allEvents = getAllProductEvents();
        const productMap = {};

        for (const [, events] of Object.entries(allEvents)) {
            for (const event of events) {
                const product = event.product || event;
                const key = product.barcode || product.name || product.id;
                if (!key) continue;

                if (!productMap[key]) {
                    productMap[key] = { product, scanCount: 0 };
                }
                productMap[key].scanCount++;
            }
        }

        // Score each product and rank
        return Object.values(productMap)
            .map(({ product, scanCount }) => {
                const assessment = getProductAssessment(product);
                return {
                    product,
                    scanCount,
                    assessment,
                    rankScore: scanCount * (signalWeight(assessment.signalLevel) + 1),
                };
            })
            .filter(item => item.assessment.signalLevel !== 'Unclear')
            .sort((a, b) => b.rankScore - a.rankScore);
    }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

    const [expandedKey, setExpandedKey] = useState(null);
    const [alternatives, setAlternatives] = useState({});
    const [loadingKey, setLoadingKey] = useState(null);

    const findAlternatives = useCallback(async (item) => {
        const key = item.product.barcode || item.product.name;
        if (alternatives[key]) {
            setExpandedKey(expandedKey === key ? null : key);
            return;
        }

        setExpandedKey(key);
        setLoadingKey(key);

        try {
            const subcat = item.product.subcategory || '';
            const cat = item.product.category || '';
            const curatedList = CURATED_ALTERNATIVES[subcat] || CURATED_ALTERNATIVES[cat];

            if (curatedList && curatedList.length > 0) {
                const curatedPromises = curatedList.map(barcode => fetchByBarcode(barcode));
                const results = await Promise.all(curatedPromises);
                
                const scored = results
                    .filter(res => res.product && String(res.product.barcode) !== String(key) && String(res.product.name) !== String(key))
                    .map(res => ({
                        product: res.product,
                        assessment: getProductAssessment(res.product),
                        isCurated: true
                    }));
                
                setAlternatives(prev => ({ ...prev, [key]: scored }));
                setLoadingKey(null);
                return;
            }

            // Search by product name or subcategory
            const searchTerm = item.product.subcategory?.replace(/-/g, ' ')
                || item.product.name.split(' ').slice(0, 2).join(' ');

            const isBeauty = item.product.category === 'personal-care';
            const searchFn = isBeauty ? searchBeautyProducts : searchProducts;
            const result = await searchFn(searchTerm, 1, 20);

            if (result.products && result.products.length > 0) {
                // Score alternatives and filter for better options
                const scored = result.products
                    .filter(p => (p.barcode || p.name) !== (item.product.barcode || item.product.name))
                    .map(p => ({
                        product: p,
                        assessment: getProductAssessment(p),
                    }))
                    .filter(alt => {
                        const altWeight = signalWeight(alt.assessment.signalLevel);
                        const origWeight = signalWeight(item.assessment.signalLevel);
                        return altWeight < origWeight || alt.assessment.totalInternalPenalty < item.assessment.totalInternalPenalty * 0.7;
                    })
                    .sort((a, b) => a.assessment.totalInternalPenalty - b.assessment.totalInternalPenalty)
                    .slice(0, 5);

                setAlternatives(prev => ({ ...prev, [key]: scored }));
            } else {
                setAlternatives(prev => ({ ...prev, [key]: [] }));
            }
        } catch (e) {
            console.warn('Alternative search failed:', e);
            setAlternatives(prev => ({ ...prev, [key]: [] }));
        } finally {
            setLoadingKey(null);
        }
    }, [alternatives, expandedKey]);

    const highImpact = rankedProducts.filter(p => signalWeight(p.assessment.signalLevel) >= 2);
    const moderate = rankedProducts.filter(p => signalWeight(p.assessment.signalLevel) === 1);

    const MIN_PRODUCTS = 3;
    const hasEnoughData = rankedProducts.length >= MIN_PRODUCTS;

    return (
        <div className="page" style={{ paddingBottom: 100 }}>
            <div className="page-header">
                <h1 className="page-title">Swap</h1>
                <p className="page-subtitle">
                    Better alternatives for your routine
                </p>
            </div>

            {!hasEnoughData ? (
                <div className="swap-empty-state">
                    <div className="swap-empty-icon">
                        <ScanBarcode size={40} color="var(--text-muted)" />
                    </div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                        Scan to unlock swaps
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                        We'll analyze your routine and suggest better alternatives.
                        Scan at least {MIN_PRODUCTS} products to get started.
                    </p>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                        {rankedProducts.length} / {MIN_PRODUCTS} products scanned
                    </div>
                    <button className="btn btn-primary" onClick={() => navigate('/')}>
                        Start scanning
                    </button>
                </div>
            ) : (
                <div className="fade-in">
                    {/* Summary */}
                    {highImpact.length > 0 && (
                        <div className="swap-summary-card">
                            <ArrowLeftRight size={18} color="#6366f1" />
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 600 }}>
                                    {highImpact.length} high-impact product{highImpact.length !== 1 ? 's' : ''} detected
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                                    Swapping these could meaningfully reduce your exposure
                                </div>
                            </div>
                        </div>
                    )}

                    {/* High impact products */}
                    {highImpact.length > 0 && (
                        <div className="swap-section">
                            <h2 className="overview-section-title">Biggest Impact Swaps</h2>
                            {highImpact.map((item) => {
                                const key = item.product.barcode || item.product.name;
                                const isExpanded = expandedKey === key;
                                const isLoading = loadingKey === key;
                                const alts = alternatives[key];

                                return (
                                    <div key={key} className="swap-product-card">
                                        <div
                                            className="swap-product-header"
                                            onClick={() => findAlternatives(item)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div className="swap-product-name">{item.product.name}</div>
                                                {item.product.brand && (
                                                    <div className="swap-product-brand">{item.product.brand}</div>
                                                )}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                                    <SignalBadge level={item.assessment.signalLevel} small />
                                                    <span className="swap-scan-count">Scanned {item.scanCount}×</span>
                                                </div>
                                            </div>
                                            <div style={{ color: 'var(--text-muted)', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                                                <ChevronRight size={20} />
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="swap-alternatives">
                                                {isLoading && (
                                                    <div className="swap-loading">
                                                        <Loader size={16} className="spin" />
                                                        <span>Searching for alternatives...</span>
                                                    </div>
                                                )}
                                                {!isLoading && alts && alts.length === 0 && (
                                                    <div className="swap-no-alts">
                                                        No lower-signal alternatives found in this category yet.
                                                    </div>
                                                )}
                                                {!isLoading && alts && alts.length > 0 && (
                                                    <>
                                                        <div className="swap-alts-title">Better options</div>
                                                        {alts.map((alt, i) => (
                                                            <div key={i} className="swap-alt-card">
                                                                <div style={{ flex: 1 }}>
                                                                    <div className="swap-alt-name">
                                                                        {alt.product.name}
                                                                        {alt.isCurated && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--bg-glass-hover)', padding: '2px 6px', borderRadius: 10, color: '#6366f1' }}>✨ Curated</span>}
                                                                    </div>
                                                                    {alt.product.brand && (
                                                                        <div className="swap-alt-brand">{alt.product.brand}</div>
                                                                    )}
                                                                </div>
                                                                <SignalBadge level={alt.assessment.signalLevel} small />
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Moderate products */}
                    {moderate.length > 0 && (
                        <div className="swap-section">
                            <h2 className="overview-section-title">Lower Priority</h2>
                            {moderate.map((item) => {
                                const key = item.product.barcode || item.product.name;
                                const isExpanded = expandedKey === key;
                                const isLoading = loadingKey === key;
                                const alts = alternatives[key];

                                return (
                                    <div key={key} className="swap-product-card">
                                        <div
                                            className="swap-product-header"
                                            onClick={() => findAlternatives(item)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div className="swap-product-name">{item.product.name}</div>
                                                {item.product.brand && (
                                                    <div className="swap-product-brand">{item.product.brand}</div>
                                                )}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                                    <SignalBadge level={item.assessment.signalLevel} small />
                                                    <span className="swap-scan-count">Scanned {item.scanCount}×</span>
                                                </div>
                                            </div>
                                            <div style={{ color: 'var(--text-muted)', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                                                <ChevronRight size={20} />
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="swap-alternatives">
                                                {isLoading && (
                                                    <div className="swap-loading">
                                                        <Loader size={16} className="spin" />
                                                        <span>Searching for alternatives...</span>
                                                    </div>
                                                )}
                                                {!isLoading && alts && alts.length === 0 && (
                                                    <div className="swap-no-alts">
                                                        No lower-signal alternatives found in this category yet.
                                                    </div>
                                                )}
                                                {!isLoading && alts && alts.length > 0 && (
                                                    <>
                                                        <div className="swap-alts-title">Better options</div>
                                                        {alts.map((alt, i) => (
                                                            <div key={i} className="swap-alt-card">
                                                                <div style={{ flex: 1 }}>
                                                                    <div className="swap-alt-name">
                                                                        {alt.product.name}
                                                                        {alt.isCurated && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--bg-glass-hover)', padding: '2px 6px', borderRadius: 10, color: '#6366f1' }}>✨ Curated</span>}
                                                                    </div>
                                                                    {alt.product.brand && (
                                                                        <div className="swap-alt-brand">{alt.product.brand}</div>
                                                                    )}
                                                                </div>
                                                                <SignalBadge level={alt.assessment.signalLevel} small />
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {highImpact.length === 0 && moderate.length === 0 && (
                        <div className="swap-empty-state" style={{ marginTop: 24 }}>
                            <ShieldCheck size={40} color="#10b981" />
                            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 12, marginBottom: 8 }}>
                                Looking good!
                            </h2>
                            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                None of your scanned products have elevated signals. Keep scanning to track your routine.
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="detail-disclaimer" style={{ marginTop: 40 }}>
                Alternatives are sourced from Open Food Facts/Beauty Facts and scored using the same engine. Results may vary in availability.
            </div>
        </div>
    );
}
